/**
 * Shared location document types for API contracts between frontend and backend
 *
 * LocationDocument links FileUpload records to Location records with metadata
 * (expiration dates, notes). Files remain company-level assets but can be
 * "attached" to locations via this linking table.
 */
import { z } from "zod";
import { uuidSchema } from "./validation-common.js";
// ===== REQUEST SCHEMAS =====
/**
 * POST /locations/:locationId/documents
 * Attach a file to a location
 */
export const AttachLocationDocumentInputSchema = z.object({
    fileUploadId: uuidSchema,
    name: z.string().max(255).optional(),
    expirationDate: z.string().datetime().optional(),
    notes: z.string().max(5000).optional(),
    permissionSetIds: z.array(z.uuid()).optional(),
});
/**
 * PATCH /locations/:locationId/documents/:documentId
 * Update location document metadata
 */
export const UpdateLocationDocumentInputSchema = z.object({
    fileUploadId: z.uuid().optional(),
    name: z.string().max(255).nullable().optional(),
    expirationDate: z.string().datetime().nullable().optional(),
    notes: z.string().max(5000).nullable().optional(),
    permissionSetIds: z.array(z.uuid()).optional(),
});
// ===== PARAMETER SCHEMAS =====
/**
 * Route params for location document operations
 */
export const LocationDocumentParamsSchema = z.object({
    locationId: uuidSchema,
    documentId: uuidSchema,
});
/**
 * Route params for location-only operations (list, attach)
 */
export const LocationIdParamsSchema = z.object({
    locationId: uuidSchema,
});
// ===== RESPONSE SCHEMAS =====
/**
 * File info embedded in location document response
 */
export const FileInfoSchema = z.object({
    id: z.uuid(),
    fileName: z.string(),
    fileSize: z.number(),
    mimeType: z.string(),
    uploadedAt: z.iso.datetime(), // ISO date string
});
/**
 * Single location document response
 */
export const LocationDocumentResponseSchema = z.object({
    id: z.uuid(),
    locationId: z.uuid(),
    locationName: z.string().nullable().optional(),
    fileUploadId: z.uuid(),
    name: z.string().nullable(),
    expirationDate: z.iso.datetime().nullable(), // ISO date string
    notes: z.string().nullable(),
    createdAt: z.iso.datetime(), // ISO date string
    createdBy: z.uuid(),
    file: FileInfoSchema,
    permissionSetIds: z.array(z.uuid()),
});
/**
 * List of location documents
 */
export const LocationDocumentListResponseSchema = z.object({
    documents: z.array(LocationDocumentResponseSchema),
});
